"use client";

import { useMemo } from "react";
import { Bot } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PreOrder } from "@/app/api/types/preOrder";
import {
  HEADER_METADATA_BLOB_FIELD,
  M3_KEYS,
  readM3CommentsObj,
} from "./cells/AiChangeBadge";
import { useHeaderAiFieldFormatter } from "../hooks/useHeaderAiFieldFormatter";
import { getLatestPendingHeaderSuggestion } from "../services/recapService";

/**
 * Read-only banner rendered above the normal `PreOrderHeader` when the V3
 * `ai_suggestions[]` array on `pre_order_metadata` carries a pending
 * **modify** entry. Purely informational: it surfaces the `Label: old → new`
 * diff so the user knows what the AI proposed without opening the
 * `/ai-pre-orders` queue.
 *
 * Approve / decline lives on the single per-pre-order mechanism in
 * `ModifyControls` ("Accept AI" / "Decline AI" at the header's right edge),
 * which already resolves every pending AI signal — header + flows — via
 * `collectAiPaths`. No action button on this banner.
 *
 * Implicit-add shells (whole pre-order at status=8 with no header block)
 * are intentionally not handled here — they're driven by `ModifyControls`
 * after the implicit-header-add fix.
 *
 * Field labels and value formatters cover the camelCase keys the AI service
 * stamps (`partnerId`, `deliveryDate`, `orderDate`, `comment`,
 * `preOrderNumber`, `customerPoNumber`, `employeeId`, `locationId`,
 * `routeId`, `shippingAddressId`). The AI executor emits these as snake_case
 * (`partner_id`, `employee_id`, …) so the banner normalizes each field key
 * to camelCase before label/value resolution. Unknown keys fall back to a
 * humanized label + stringified value.
 */
interface AiHeaderChangesBannerProps {
  preOrder: PreOrder;
}

const KNOWN_LABELS: Record<string, string> = {
  partnerId: "Partner",
  deliveryDate: "Delivery date",
  orderDate: "Order date",
  comment: "Comment",
  preOrderNumber: "Pre-order #",
  customerPoNumber: "Customer PO #",
  employeeId: "Employee",
  locationId: "Location",
  routeId: "Route",
  shippingAddressId: "Shipping address",
  m3Comments: "M3 comments",
};

/** Convert `snake_case` → `camelCase`; leave already-camel keys untouched. */
function toCamelCase(field: string): string {
  if (!field.includes("_")) return field;
  return field.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
}

export function AiHeaderChangesBanner({
  preOrder,
}: Readonly<AiHeaderChangesBannerProps>) {
  const { t } = useTranslation();
  const formatHeaderValue = useHeaderAiFieldFormatter();

  const pending = getLatestPendingHeaderSuggestion(preOrder);

  const rows = useMemo(() => {
    if (!pending || pending.type !== "modify" || !pending.changes) return [];
    const out: Array<{
      field: string;
      label: string;
      from: string;
      to: string;
    }> = [];
    for (const [rawField, change] of Object.entries(pending.changes)) {
      // The `preOrderMetadata` sentinel carries a full pre_order_metadata
      // blob in `to`. We do NOT render it as a single opaque row —
      // instead we extract the user-visible sub-keys we know about
      // (today: `m3_comments`) and emit them as their own rows. Unknown
      // sub-keys stay invisible until they get their own opt-in entry.
      if (rawField === HEADER_METADATA_BLOB_FIELD) {
        const m3Label = t(
          "aiProposals.headerField.m3Comments",
          KNOWN_LABELS.m3Comments,
        );
        const m3Row = extractM3CommentsRow(change, m3Label);
        if (m3Row) out.push(m3Row);
        continue;
      }
      const field = toCamelCase(rawField);
      const known = KNOWN_LABELS[field];
      const label = known
        ? t(`aiProposals.headerField.${field}`, known)
        : humanizeField(field);
      out.push({
        field: rawField,
        label,
        from: formatHeaderValue(field, change.from),
        to: formatHeaderValue(field, change.to),
      });
    }
    return out;
  }, [pending, formatHeaderValue, t]);

  if (!pending || pending.type !== "modify" || rows.length === 0) return null;

  return (
    <div
      className="bg-violet-50/50 border-b border-violet-200 text-xs"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      data-testid="ai-header-changes-banner"
    >
      <div className="flex items-center gap-1.5 px-3 py-1">
        <Bot className="h-3.5 w-3.5 text-violet-600 shrink-0" />
        <span className="font-medium text-violet-700 truncate">
          {t("aiProposals.headerChanges", "AI suggested changes")}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 px-3 pb-1 pl-9">
        {rows.map((row) => (
          <span key={row.field} className="text-violet-700">
            <span className="text-violet-500">{row.label}:</span>{" "}
            <span className="line-through text-violet-400">{row.from}</span> →{" "}
            <span className="font-medium">{row.to}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Fallback humanizer for unknown camelCase keys → spaced sentence case. */
function humanizeField(field: string): string {
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Extract the `m3_comments` slot from a staged `preOrderMetadata` blob
 * change, formatted as a single row for the banner. Returns null when
 * the blob carries no m3_comments diff (e.g. AI only patched `nb_colis`).
 *
 * The row's `from` / `to` show only the sub-keys that actually moved,
 * formatted as `c1: "X" → "Y", c5: "" → "Z"`. Empty diffs collapse to
 * a "—" so the user sees a slot was cleared rather than nothing.
 *
 * `readM3CommentsObj` / `M3_KEYS` are shared with `AiChangeBadge.tsx` —
 * the badge and banner stay in lockstep on slot ordering + coercion.
 */
function extractM3CommentsRow(
  change: { from: unknown; to: unknown; finalTo?: unknown },
  label: string,
): { field: string; label: string; from: string; to: string } | null {
  const readBlobM3 = (blob: unknown): Record<string, string> | null => {
    if (!blob || typeof blob !== "object" || Array.isArray(blob)) return null;
    return readM3CommentsObj((blob as Record<string, unknown>).m3_comments);
  };
  const fromM3 = readBlobM3(change.from);
  const toM3 = readBlobM3(change.to);
  if (fromM3 == null && toM3 == null) return null;
  const movedKeys: string[] = [];
  for (const k of M3_KEYS) {
    const a = fromM3?.[k] ?? "";
    const b = toM3?.[k] ?? "";
    if (a !== b) movedKeys.push(k);
  }
  if (movedKeys.length === 0) return null;
  const fmt = (m: Record<string, string> | null) =>
    movedKeys.map((k) => `${k}: ${m?.[k] ? `"${m[k]}"` : "—"}`).join(", ");
  return {
    field: "m3Comments",
    label,
    from: fmt(fromM3),
    to: fmt(toM3),
  };
}
